import { useGT } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  useOverrideOption,
  useFulfillmentCandidates,
  type GetSuggestionDetailResponse,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import { buildOverrideSectionSubmitArgs } from './buildOverrideSectionSubmitArgs';
import {
  createOverrideFormDraft,
  type OverrideFormDraft,
} from './overrideFormDraft';
import { overrideSectionBulkScope } from './overrideSectionBulkScope';
import {
  deriveOverrideEffectiveState,
  mergeOverrideTransferSources,
  overrideFormCanSubmit,
  resolveCurrentOverrideOption,
} from './overrideSectionDerivedState';
import { executeOverrideSubmit } from './overrideSectionSubmitHandlers';
import { createOverrideSubmitInFlight } from './overrideSubmitInFlight';
import type {
  OverrideSectionSource,
  OverrideSectionVendor,
} from './overrideSectionTypes';

export type { OverrideSectionSource, OverrideSectionVendor };

export function useOverrideSectionController({
  suggestion,
  side,
  highlightCartKey,
}: {
  suggestion: GetSuggestionDetailResponse;
  side?: 'buy' | 'transfer';
  highlightCartKey: string | null;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const override = useOverrideOption();
  const { bulkLineCount, linesForBulk } = overrideSectionBulkScope(
    highlightCartKey,
    suggestion,
  );
  const [open, setOpen] = useState(false);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const [submitLock] = useState(() => createOverrideSubmitInFlight());
  const [formDraft, setFormDraft] = useState(() =>
    createOverrideFormDraft(suggestion.neededQuantity),
  );

  const candidates = useFulfillmentCandidates(
    {
      productV3Key: suggestion.productV3Key ?? '',
      destinationWarehouseId: suggestion.destinationWarehouseId,
    },
    open,
  );
  const vendors: OverrideSectionVendor[] = candidates.data?.vendors ?? [];
  const rawSources = candidates.data?.transferSources ?? [];
  const currentOption = resolveCurrentOverrideOption(suggestion);
  const sources = mergeOverrideTransferSources(rawSources, currentOption);
  const effective = deriveOverrideEffectiveState({
    side,
    formDraft,
    currentOption,
    sources,
    vendors,
    bulkLineCount,
  });
  const pending = override.isPending || submitInFlight;
  const canSubmit = overrideFormCanSubmit(
    effective,
    pending,
    candidates.isLoading,
  );
  const submitArgs = buildOverrideSectionSubmitArgs({
    queryClient,
    gt,
    suggestion,
    bulkLineCount,
    linesForBulk,
    effective,
    overrideMutateAsync: (input) => override.mutateAsync(input),
  });

  function openOverrideForm() {
    setFormDraft(createOverrideFormDraft(suggestion.neededQuantity));
    setOpen(true);
  }

  function patchFormDraft(patch: Partial<OverrideFormDraft>) {
    setFormDraft((draft) => ({ ...draft, ...patch }));
  }

  function acquireSubmitLock(): boolean {
    if (!submitLock.tryAcquire()) return false;
    setSubmitInFlight(true);
    return true;
  }

  function releaseSubmitLock() {
    submitLock.release();
    setSubmitInFlight(false);
  }

  function submit() {
    if (!canSubmit || !acquireSubmitLock()) return Promise.resolve();
    return executeOverrideSubmit(canSubmit, submitArgs, {
      onClose: () => setOpen(false),
      onVendorCandidatesError: () =>
        toast.error(
          gt('Could not load vendors for the selected lines.', {
            context: 'bulk fulfillment override candidates error',
          }),
        ),
    }).finally(releaseSubmitLock);
  }

  return {
    open,
    setOpen,
    openOverrideForm,
    bulkLineCount,
    candidatesLoading: candidates.isLoading,
    vendors,
    sources,
    formDraft,
    patchFormDraft,
    effectiveAction: effective.effectiveAction,
    actionChoices: effective.actionChoices,
    effectiveVendorId: effective.effectiveVendorId,
    effectiveSourceId: effective.effectiveSourceId,
    selectedVendor: effective.selectedVendor,
    isBuy: effective.isBuy,
    isTransfer: effective.isTransfer,
    qty: effective.qty,
    canSubmit,
    submit,
    isSaving: pending,
  };
}
